// Utility Functions for Librario

import { CacheTime, CONTENT_TYPES, ContentType } from './types';
import { debugConsole } from './debug';

/**
 * Show toast notification
 */
export function showToast(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
  // Remove existing toast
  const existingToast = document.querySelector('.toast');
  if (existingToast) {
    existingToast.remove();
  }

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;

  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.animation = 'slideOutRight 0.3s ease-out';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

/**
 * Convert cache time to seconds
 */
export function convertCacheTime(value: number, unit: CacheTime['unit']): number {
  switch (unit) {
    case 'seconds':
      return value;
    case 'minutes':
      return value * 60;
    case 'hours':
      return value * 3600;
  }
}

/**
 * Convert seconds to largest applicable unit
 */
export function getLargestUnit(seconds: number): CacheTime {
  if (seconds % 3600 === 0) {
    return { value: seconds / 3600, unit: 'hours' };
  }
  if (seconds % 60 === 0) {
    return { value: seconds / 60, unit: 'minutes' };
  }
  return { value: seconds, unit: 'seconds' };
}

/**
 * Toggle collapsible section
 */
export function toggleSection(headerId: string, contentId: string): void {
  const header = document.getElementById(headerId);
  const content = document.getElementById(contentId);

  if (!header || !content) return;

  const isHidden = content.style.display === 'none' || content.style.display === '';

  if (isHidden) {
    content.style.display = 'block';
    header.classList.add('active');
  } else {
    content.style.display = 'none';
    header.classList.remove('active');
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    }
  } catch (err) {
    console.error('Copy failed:', err);
    return false;
  }
}

/**
 * Toggle password visibility
 */
export function setupPasswordToggle(inputId: string, buttonId: string): void {
  const input = document.getElementById(inputId) as HTMLInputElement;
  const button = document.getElementById(buttonId) as HTMLButtonElement;

  if (!input || !button) return;

  button.addEventListener('click', () => {
    if (input.type === 'password') {
      input.type = 'text';
      // Change icon to "eye-off" by updating the SVG path
      const svg = button.querySelector('.eye-icon');
      if (svg) {
        svg.innerHTML = `
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        `;
      }
    } else {
      input.type = 'password';
      // Change icon back to "eye" by updating the SVG path
      const svg = button.querySelector('.eye-icon');
      if (svg) {
        svg.innerHTML = `
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        `;
      }
    }
  });
}

/**
 * Validate auth key format
 */
export function validateAuthKey(authKey: string): { valid: boolean; message?: string } {
  if (!authKey || authKey.trim() === '') {
    return { valid: false, message: 'Auth key is required' };
  }

  if (authKey.length < 10) {
    return { valid: false, message: 'Auth key seems too short' };
  }

  return { valid: true };
}

/**
 * Cache TTL limits (set by initApp from HTML data attributes)
 */
let minCacheTTL = 300;   // Default fallback: 5 minutes
let maxCacheTTL = 86400; // Default fallback: 24 hours

/**
 * Update cache TTL limits (called from main.ts after reading HTML data attributes)
 */
export function setCacheTTLLimits(min: number, max: number): void {
  minCacheTTL = min;
  maxCacheTTL = max;
}

/**
 * Format seconds to human-readable string using largest time unit
 * Examples: 3600 -> "1 hour", 7200 -> "2 hours", 300 -> "5 minutes"
 */
export function formatSeconds(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds / 60);

  if (days > 0 && seconds % 86400 === 0) {
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (hours > 0 && seconds % 3600 === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (minutes > 0 && seconds % 60 === 0) {
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

/**
 * Validate cache validity with dynamic limits
 */
export function validateCacheValidity(seconds: number): { valid: boolean; message?: string } {
  if (isNaN(seconds)) {
    return { valid: false, message: 'Cache validity must be a number' };
  }

  if (seconds < minCacheTTL || seconds > maxCacheTTL) {
    // Format limits as human-readable
    const minFormatted = formatSeconds(minCacheTTL);
    const maxFormatted = formatSeconds(maxCacheTTL);
    return {
      valid: false,
      message: `Cache validity must be between ${minFormatted} and ${maxFormatted}`,
    };
  }

  return { valid: true };
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Generate unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Validates catalog configuration
 */
export function validateCatalogConfig(catalogs: any[]): { valid: boolean; message?: string } {
  debugConsole.log(`Starting catalog configuration validation (${catalogs?.length || 0} catalogs)`);

  if (!Array.isArray(catalogs)) {
    debugConsole.log('Validation failed: catalogs is not an array');
    return { valid: false, message: 'Catalogs must be an array' };
  }

  if (catalogs.length === 0) {
    debugConsole.log('Validation failed: no catalogs configured');
    return { valid: false, message: 'At least one catalog must be configured' };
  }

  for (let i = 0; i < catalogs.length; i++) {
    const catalog = catalogs[i];
    debugConsole.log(`Validating catalog ${i + 1}: id=${catalog.id}, name=${catalog.name}, type=${catalog.type}`);

    // Validate catalog name
    if (!catalog.name || typeof catalog.name !== 'string') {
      debugConsole.log(`Catalog ${i + 1} validation failed: name is missing or not a string`);
      return { valid: false, message: `Catalog ${i + 1}: Name is required` };
    }

    if (catalog.name.trim().length === 0) {
      debugConsole.log(`Catalog ${i + 1} validation failed: name is empty after trim`);
      return { valid: false, message: `Catalog ${i + 1}: Name cannot be empty` };
    }

    if (catalog.name.length > 100) {
      debugConsole.log(`Catalog ${i + 1} validation failed: name length ${catalog.name.length} exceeds 100 characters`);
      return { valid: false, message: `Catalog ${i + 1}: Name cannot exceed 100 characters` };
    }

    // Validate catalog type (must be one of the valid types)
    if (!catalog.type || typeof catalog.type !== 'string') {
      debugConsole.log(`Catalog ${i + 1} validation failed: type is missing or not a string`);
      return { valid: false, message: `Catalog ${i + 1}: Type is required` };
    }

    if (catalog.type.trim().length === 0) {
      debugConsole.log(`Catalog ${i + 1} validation failed: type is empty after trim`);
      return { valid: false, message: `Catalog ${i + 1}: Type cannot be empty` };
    }

    const validTypes = [...CONTENT_TYPES, 'mixed'];
    if (!validTypes.includes(catalog.type as ContentType | 'mixed')) {
      debugConsole.log(`Catalog ${i + 1} validation failed: type '${catalog.type}' is not valid (must be one of: ${validTypes.join(', ')})`);
      return { valid: false, message: `Catalog ${i + 1}: Invalid type '${catalog.type}'` };
    }

    debugConsole.log(`Catalog ${i + 1}: Type '${catalog.type}' is valid`);


    // Validate catalog ID
    if (!catalog.id || typeof catalog.id !== 'string') {
      debugConsole.log(`Catalog ${i + 1} validation failed: ID is missing or not a string`);
      return { valid: false, message: `Catalog ${i + 1}: ID is required` };
    }

    if (!/^[a-z0-9-]+$/.test(catalog.id)) {
      debugConsole.log(`Catalog ${i + 1} validation failed: ID '${catalog.id}' contains invalid characters`);
      return { valid: false, message: `Catalog ${i + 1}: ID must contain only lowercase letters, numbers, and hyphens` };
    }

    debugConsole.log(`Catalog ${i + 1}: ID '${catalog.id}' is valid`);

    // Validate limit (optional, but if present must be valid)
    if (catalog.limit !== undefined && catalog.limit !== null) {
      if (typeof catalog.limit !== 'number' || catalog.limit < 0) {
        debugConsole.log(`Catalog ${i + 1} validation failed: limit ${catalog.limit} is invalid`);
        return { valid: false, message: `Catalog ${i + 1}: Limit must be a positive number or 0 for unlimited` };
      }

      if (catalog.limit > 1000) {
        debugConsole.log(`Catalog ${i + 1} validation failed: limit ${catalog.limit} exceeds 1000`);
        return { valid: false, message: `Catalog ${i + 1}: Limit cannot exceed 1000 items` };
      }

      debugConsole.log(`Catalog ${i + 1}: Limit ${catalog.limit} is valid`);
    }

    // Validate position (should be a number)
    if (catalog.position !== undefined && typeof catalog.position !== 'number') {
      debugConsole.log(`Catalog ${i + 1} validation failed: position is not a number`);
      return { valid: false, message: `Catalog ${i + 1}: Position must be a number` };
    }

    debugConsole.log(`Catalog ${i + 1}: All validations passed`);
  }

  // Check for duplicate IDs
  debugConsole.log('Checking for duplicate catalog IDs...');
  const ids = catalogs.map(c => c.id);
  const uniqueIds = new Set(ids);
  if (ids.length !== uniqueIds.size) {
    const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
    debugConsole.log(`Validation failed: Found duplicate catalog IDs: ${duplicates.join(', ')}`);
    return { valid: false, message: 'Duplicate catalog IDs found. Each catalog must have a unique ID' };
  }
  debugConsole.log('No duplicate IDs found');

  // Check for duplicate name+type combinations (not just names)
  // Same name can be used for different types, but not within the same type
  debugConsole.log('Checking for duplicate name+type combinations...');
  const compositeKeys = catalogs.map(c => `${c.name}|||${c.type}`);
  const uniqueCompositeKeys = new Set(compositeKeys);
  if (compositeKeys.length !== uniqueCompositeKeys.size) {
    // Find which catalogs are duplicates
    const seenKeys = new Map<string, number>();
    const duplicateIndices: number[] = [];

    compositeKeys.forEach((key, index) => {
      if (seenKeys.has(key)) {
        duplicateIndices.push(index);
        if (!duplicateIndices.includes(seenKeys.get(key)!)) {
          duplicateIndices.push(seenKeys.get(key)!);
        }
      } else {
        seenKeys.set(key, index);
      }
    });

    const duplicateNames = duplicateIndices.map(i => `"${catalogs[i].name}" (${catalogs[i].type})`).join(', ');
    debugConsole.log(`Validation failed: Found duplicate name+type combinations: ${duplicateNames}`);
    return {
      valid: false,
      message: `Duplicate catalog found: ${duplicateNames}. Same name can be used for different types, but not within the same type.`
    };
  }
  debugConsole.log('No duplicate name+type combinations found');

  debugConsole.log('All catalog configuration validations passed successfully');
  return { valid: true };
}
